/**
 * Dev-only tweaks panel: live theme/accent/density toggles + jump-to links.
 * Mounts only in dev (import.meta.env.DEV). Persists to localStorage 'bm-tweaks'.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'bm-tweaks';

export type Theme = 'light' | 'dark';
export type Density = 'compact' | 'comfortable';

export interface Tweaks {
  theme: Theme;
  accent: string;
  density: Density;
}

const DEFAULTS: Tweaks = {
  theme: 'dark',
  accent: '#c64a32',
  density: 'comfortable',
};

const ACCENT_SWATCHES = ['#c64a32', '#1c1c1c', '#2563a5', '#2d7d4f'];

function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Tweaks>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

function applyTweaks(t: Tweaks) {
  const html = document.documentElement;
  html.setAttribute('data-theme', t.theme);
  html.style.setProperty('--bm-accent', t.accent);
  html.style.setProperty('--bm-density', t.density === 'compact' ? '0.85' : '1');
}

export function useTweaks(): [Tweaks, <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void] {
  const [tweaks, setTweaks] = useState<Tweaks>(loadTweaks);

  useEffect(() => {
    applyTweaks(tweaks);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
    } catch {
      /* ignore quota */
    }
  }, [tweaks]);

  const set = useCallback(<K extends keyof Tweaks>(key: K, value: Tweaks[K]) => {
    setTweaks((prev) => ({ ...prev, [key]: value }));
  }, []);

  return [tweaks, set];
}

/* ─── Panel ─────────────────────────────────────────────────────────── */

const panelStyles = `
  .twk-panel { position: fixed; right: 16px; bottom: 16px; z-index: 50;
    width: 260px; max-height: calc(100vh - 32px); display: flex; flex-direction: column;
    background: var(--bm-surface-1); color: var(--bm-text);
    border: 1px solid var(--bm-border); border-radius: var(--bm-r-lg);
    box-shadow: var(--bm-elev-pop);
    font: 12px/1.5 var(--bm-font); overflow: hidden; }
  .twk-hd { appearance: none; width: 100%; display: flex;
    align-items: center; justify-content: space-between;
    background: var(--bm-surface-1); color: var(--bm-text);
    font: inherit; text-align: left;
    border: 0; border-bottom: 1px solid var(--bm-border-soft);
    padding: 10px 10px 10px 14px; user-select: none; cursor: pointer; }
  .twk-hd:hover { background: var(--bm-tint-1); }
  .twk-hd b { font-size: 12px; font-weight: 600; letter-spacing: .02em; color: var(--bm-text); }
  .twk-x { width: 22px; height: 22px; border-radius: 4px;
    color: var(--bm-text-muted); display: inline-flex; align-items: center;
    justify-content: center; font-size: 14px; line-height: 1; cursor: pointer; }
  .twk-x:hover { background: var(--bm-tint-2); color: var(--bm-text); }
  .twk-body { padding: 10px 14px 14px; display: flex; flex-direction: column;
    gap: 10px; overflow-y: auto; min-height: 0; }
  .twk-sect { font-size: 10px; font-weight: 600; letter-spacing: .08em;
    text-transform: uppercase; color: var(--bm-text-muted); padding: 8px 0 0; }
  .twk-sect:first-child { padding-top: 0; }
  .twk-row { display: flex; flex-direction: column; gap: 5px; }
  .twk-row-h { flex-direction: row; align-items: center; justify-content: space-between; gap: 10px; }
  .twk-lbl { color: var(--bm-text-soft); font-weight: 500; }
  .twk-seg { display: inline-flex; padding: 2px;
    background: var(--bm-surface-recess); border: 1px solid var(--bm-border-soft);
    border-radius: 6px; gap: 2px; }
  .twk-seg button { appearance: none; background: transparent; border: 0;
    color: var(--bm-text-muted); font: inherit; padding: 4px 10px;
    border-radius: 4px; cursor: pointer; }
  .twk-seg button[data-active="true"] { background: var(--bm-surface-1);
    color: var(--bm-text); box-shadow: 0 0 0 0.5px var(--bm-border) inset; }
  .twk-swatches { display: flex; gap: 6px; }
  .twk-sw { width: 22px; height: 22px; border-radius: 50%; cursor: pointer;
    border: 1.5px solid transparent; transition: transform 120ms var(--bm-ease); }
  .twk-sw:hover { transform: scale(1.1); }
  .twk-sw[data-active="true"] { border-color: var(--bm-text); }
  .twk-jump button { appearance: none; background: transparent; border: 0;
    color: var(--bm-text-soft); font: inherit; text-align: left;
    padding: 4px 6px; border-radius: 4px; cursor: pointer; width: 100%; }
  .twk-jump button:hover { background: var(--bm-tint-2); color: var(--bm-text); }
  .twk-toggle-btn { position: fixed; right: 16px; bottom: 16px; z-index: 49;
    width: 36px; height: 36px; border-radius: 50%;
    background: var(--bm-surface-1); border: 1px solid var(--bm-border);
    color: var(--bm-text-soft); display: inline-flex; align-items: center;
    justify-content: center; cursor: pointer; box-shadow: var(--bm-elev-card); }
  .twk-toggle-btn:hover { color: var(--bm-text); }
`;

function ensureStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('bm-twk-styles')) return;
  const el = document.createElement('style');
  el.id = 'bm-twk-styles';
  el.textContent = panelStyles;
  document.head.appendChild(el);
}

interface TweaksPanelProps {
  tweaks: Tweaks;
  setTweak: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
  onOpenPalette?: () => void;
}

export function TweaksPanel({ tweaks, setTweak, onOpenPalette }: TweaksPanelProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const firedRef = useRef(false);

  useEffect(() => {
    ensureStyles();
  }, []);

  if (!open) {
    return (
      <button
        type="button"
        className="twk-toggle-btn"
        title="Open tweaks (dev only)"
        onClick={() => setOpen(true)}
      >
        ⚙
      </button>
    );
  }

  return (
    <div className="twk-panel" role="dialog" aria-label="Tweaks">
      <button
        type="button"
        className="twk-hd"
        onClick={() => setOpen(false)}
        aria-label="Close tweaks"
      >
        <b>Tweaks</b>
        <span className="twk-x">×</span>
      </button>
      <div className="twk-body">
        <div className="twk-sect">Theme</div>
        <div className="twk-row twk-row-h">
          <span className="twk-lbl">Mode</span>
          <Segmented
            value={tweaks.theme}
            options={['light', 'dark'] as const}
            onChange={(v) => setTweak('theme', v)}
          />
        </div>
        <div className="twk-row twk-row-h">
          <span className="twk-lbl">Accent</span>
          <div className="twk-swatches">
            {ACCENT_SWATCHES.map((c) => (
              <button
                type="button"
                key={c}
                className="twk-sw"
                data-active={tweaks.accent === c}
                style={{ background: c }}
                aria-label={`Accent ${c}`}
                onClick={() => setTweak('accent', c)}
              />
            ))}
          </div>
        </div>

        <div className="twk-sect">Density</div>
        <div className="twk-row twk-row-h">
          <span className="twk-lbl">Rows</span>
          <Segmented
            value={tweaks.density}
            options={['compact', 'comfortable'] as const}
            onChange={(v) => setTweak('density', v)}
          />
        </div>

        <div className="twk-sect">Jump to</div>
        <div className="twk-jump twk-row">
          {onOpenPalette && (
            <button
              type="button"
              onClick={() => {
                if (firedRef.current) return;
                firedRef.current = true;
                setTimeout(() => {
                  firedRef.current = false;
                }, 200);
                onOpenPalette();
              }}
            >
              Open command palette ⌘K
            </button>
          )}
          <button type="button" onClick={() => navigate('/dashboard')}>
            Dashboard
          </button>
          <button type="button" onClick={() => navigate('/accounts')}>
            Accounts
          </button>
          <button type="button" onClick={() => navigate('/admin/settings')}>
            Admin · Settings
          </button>
          <button type="button" onClick={() => navigate('/admin/audit')}>
            Admin · Audit log
          </button>
          <button type="button" onClick={() => navigate('/admin/api-keys')}>
            Admin · API keys
          </button>
          <button type="button" onClick={() => navigate('/logged-out')}>
            Logged out splash
          </button>
        </div>
      </div>
    </div>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="twk-seg">
      {options.map((opt) => (
        <button key={opt} type="button" data-active={value === opt} onClick={() => onChange(opt)}>
          {opt}
        </button>
      ))}
    </div>
  );
}
